import React, { useState } from 'react'
import {
  Card,
  Table,
  Button,
  Modal,
  Input,
  Space,
  Typography,
  message,
  Tag,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DownloadOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import CertificatePositionEditor from './CertificatePositionEditor'
import { CertificateConfig } from '../../../utils/certificateGenerator'

const { Title, Text } = Typography

interface CertificateRecord {
  id: string
  studentName: string
  courseName: string
  issueDate: string
  config: CertificateConfig
  status: 'pending' | 'issued' | 'downloaded'
}

const CertificateManagement: React.FC = () => {
  const [certificates, setCertificates] = useState<CertificateRecord[]>([
    {
      id: '1',
      studentName: 'John Doe',
      courseName: 'React Development',
      issueDate: '2024-01-15',
      config: {
        name: 'John Doe',
        // x: undefined, // Auto-centered based on text length
        y: 400,
        courseName: 'React Development',
        // courseX: undefined, // Auto-centered based on text length
        courseY: 320,
        fontSize: 24,
        fontColor: { r: 42, g: 170, b: 250 },
        courseFontSize: 18,
        courseFontColor: { r: 51, g: 51, b: 51 },
      },
      status: 'issued',
    },
    {
      id: '2',
      studentName: 'Jane Smith',
      courseName: 'TypeScript Fundamentals',
      issueDate: '2024-01-16',
      config: {
        name: 'Jane Smith',
        // x: undefined, // Auto-centered based on text length
        y: 420,
        courseName: 'TypeScript Fundamentals',
        // courseX: undefined, // Auto-centered based on text length
        courseY: 320,
        fontSize: 28,
        fontColor: { r: 42, g: 170, b: 250 },
        courseFontSize: 18,
        courseFontColor: { r: 51, g: 51, b: 51 },
      },
      status: 'downloaded',
    },
  ])

  const [isEditorVisible, setIsEditorVisible] = useState(false)
  const [
    selectedCertificate,
    setSelectedCertificate,
  ] = useState<CertificateRecord | null>(null)
  const [isAddModalVisible, setIsAddModalVisible] = useState(false)
  const [newCertificate, setNewCertificate] = useState({
    studentName: '',
    courseName: '',
    config: {
      name: '',
      x: undefined,
      y: 400,
      courseName: '',
      courseX: undefined,
      courseY: 320,
      fontSize: 24,
      fontColor: { r: 42, g: 170, b: 250 },
      courseFontSize: 18,
      courseFontColor: { r: 51, g: 51, b: 51 },
    },
  })

  const handleEditCertificate = (record: CertificateRecord) => {
    setSelectedCertificate(record)
    setIsEditorVisible(true)
  }

  const handleSaveConfig = (config: CertificateConfig) => {
    if (selectedCertificate) {
      setCertificates((prev) =>
        prev.map((cert) =>
          cert.id === selectedCertificate.id ? { ...cert, config } : cert,
        ),
      )
      message.success('Konfigurasi sertifikat berhasil disimpan')
    }
  }

  const handleAddCertificate = () => {
    if (!newCertificate.studentName || !newCertificate.courseName) {
      message.error('Nama siswa dan nama kursus harus diisi')
      return
    }

    const certificate: CertificateRecord = {
      id: Date.now().toString(),
      studentName: newCertificate.studentName,
      courseName: newCertificate.courseName,
      issueDate: new Date().toISOString().split('T')[0],
      config: {
        ...newCertificate.config,
        name: newCertificate.studentName,
        courseName: newCertificate.courseName,
      },
      status: 'pending',
    }

    setCertificates((prev) => [...prev, certificate])
    setNewCertificate({
      studentName: '',
      courseName: '',
      config: {
        name: '',
        x: undefined, // Auto-centered based on text length
        y: 400,
        courseName: '',
        courseX: undefined, // Auto-centered based on text length
        courseY: 320,
        fontSize: 24,
        fontColor: { r: 42, g: 170, b: 250 },
        courseFontSize: 18,
        courseFontColor: { r: 51, g: 51, b: 51 },
      },
    })
    setIsAddModalVisible(false)
    message.success('Sertifikat baru berhasil ditambahkan')
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'orange'
      case 'issued':
        return 'blue'
      case 'downloaded':
        return 'green'
      default:
        return 'default'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Menunggu'
      case 'issued':
        return 'Diterbitkan'
      case 'downloaded':
        return 'Diunduh'
      default:
        return status
    }
  }

  const columns = [
    {
      title: 'Nama Siswa',
      dataIndex: 'studentName',
      key: 'studentName',
      sorter: (a: CertificateRecord, b: CertificateRecord) =>
        a.studentName.localeCompare(b.studentName),
    },
    {
      title: 'Nama Kursus',
      dataIndex: 'courseName',
      key: 'courseName',
      sorter: (a: CertificateRecord, b: CertificateRecord) =>
        a.courseName.localeCompare(b.courseName),
    },
    {
      title: 'Tanggal Terbit',
      dataIndex: 'issueDate',
      key: 'issueDate',
      sorter: (a: CertificateRecord, b: CertificateRecord) =>
        new Date(a.issueDate).getTime() - new Date(b.issueDate).getTime(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
      filters: [
        { text: 'Menunggu', value: 'pending' },
        { text: 'Diterbitkan', value: 'issued' },
        { text: 'Diunduh', value: 'downloaded' },
      ],
      onFilter: (value: string, record: CertificateRecord) =>
        record.status === value,
    },
    {
      title: 'Aksi',
      key: 'action',
      render: (text: string, record: CertificateRecord) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditCertificate(record)}
          >
            Edit
          </Button>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => {
              setSelectedCertificate(record)
              setIsEditorVisible(true)
            }}
          >
            Preview
          </Button>
          <Button
            icon={<DownloadOutlined />}
            size="small"
            onClick={() => {
              // Update status to downloaded
              setCertificates((prev) =>
                prev.map((cert) =>
                  cert.id === record.id
                    ? { ...cert, status: 'downloaded' as const }
                    : cert,
                ),
              )
              message.success('Status diubah menjadi diunduh')
            }}
          >
            Download
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: '20px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <Title level={2}>Manajemen Sertifikat</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsAddModalVisible(true)}
        >
          Tambah Sertifikat
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={certificates}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} sertifikat`,
          }}
        />
      </Card>

      {/* Certificate Editor Modal */}
      <Modal
        title="Editor Sertifikat"
        open={isEditorVisible}
        onCancel={() => {
          setIsEditorVisible(false)
          setSelectedCertificate(null)
        }}
        footer={null}
        width="90%"
        style={{ top: 20 }}
      >
        {selectedCertificate && (
          <CertificatePositionEditor
            studentName={selectedCertificate.studentName}
            onSave={handleSaveConfig}
          />
        )}
      </Modal>

      {/* Add Certificate Modal */}
      <Modal
        title="Tambah Sertifikat Baru"
        open={isAddModalVisible}
        onOk={handleAddCertificate}
        onCancel={() => {
          setIsAddModalVisible(false)
          setNewCertificate({
            studentName: '',
            courseName: '',
            config: {
              name: '',
              x: undefined,
              y: 400,
              courseName: '',
              courseX: undefined,
              courseY: 320,
              fontSize: 24,
              fontColor: { r: 42, g: 170, b: 250 },
              courseFontSize: 18,
              courseFontColor: { r: 51, g: 51, b: 51 },
            },
          })
        }}
        okText="Tambah"
        cancelText="Batal"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong>Nama Siswa:</Text>
            <Input
              value={newCertificate.studentName}
              onChange={(e) => {
                setNewCertificate((prev) => ({
                  ...prev,
                  studentName: e.target.value,
                  config: { ...prev.config, name: e.target.value },
                }))
              }}
              placeholder="Masukkan nama siswa"
              style={{ marginTop: 8 }}
            />
          </div>
          <div>
            <Text strong>Nama Kursus:</Text>
            <Input
              value={newCertificate.courseName}
              onChange={(e) =>
                setNewCertificate((prev) => ({
                  ...prev,
                  courseName: e.target.value,
                }))
              }
              placeholder="Masukkan nama kursus"
              style={{ marginTop: 8 }}
            />
          </div>
        </Space>
      </Modal>
    </div>
  )
}

export default CertificateManagement
